import { type GrenadeId, GRENADES as GRENADE_CONFIGS, MAX_FLASHBANGS, type WeaponId } from '../core/config';
import { type GrenadeInventory, canCarryGrenade } from '../gameplay/grenade';
import { BuyPresetManager, summarizePresetLoadout, type BuyPreset } from './buy-presets';
import { playUISelect, playUIError } from '../audio/sounds';
import { audioManager } from '../audio/audio-manager';

interface WeaponDef {
  id: WeaponId;
  name: string;
  price: number;
  key: string;
  category: string;
  team?: 'T' | 'CT'; // Team restriction (undefined = available to both)
}

interface EquipmentDef {
  id: string;
  name: string;
  price: number;
  key: string;
  team?: 'T' | 'CT';
}

interface GrenadeDef {
  id: GrenadeId;
  name: string;
  price: number;
  key: string;
  team?: 'T' | 'CT';
  maxCarry: number;
}

const WEAPONS: WeaponDef[] = [
  { id: 'glock', name: 'Glock-18', price: 200, key: '1', category: 'Pistols', team: 'T' },
  { id: 'usp', name: 'USP-S', price: 200, key: '2', category: 'Pistols', team: 'CT' },
  { id: 'p250', name: 'P250', price: 300, key: '3', category: 'Pistols' },
  { id: 'deagle', name: 'Desert Eagle', price: 700, key: '4', category: 'Pistols' },
  { id: 'fiveseven', name: 'Five-SeveN', price: 500, key: '5', category: 'Pistols', team: 'CT' },
  { id: 'tec9', name: 'Tec-9', price: 500, key: '6', category: 'Pistols', team: 'T' },
  { id: 'mp9', name: 'MP9', price: 1250, key: '1', category: 'SMGs', team: 'CT' },
  { id: 'mac10', name: 'MAC-10', price: 1050, key: '2', category: 'SMGs', team: 'T' },
  { id: 'p90', name: 'P90', price: 2350, key: '3', category: 'SMGs' },
  { id: 'famas', name: 'FAMAS', price: 2050, key: '1', category: 'Rifles', team: 'CT' },
  { id: 'galil', name: 'Galil AR', price: 1800, key: '2', category: 'Rifles', team: 'T' },
  { id: 'ak47', name: 'AK-47', price: 2700, key: '3', category: 'Rifles', team: 'T' },
  { id: 'm4a4', name: 'M4A4', price: 3100, key: '4', category: 'Rifles', team: 'CT' },
  { id: 'm4a1s', name: 'M4A1-S', price: 2900, key: '5', category: 'Rifles', team: 'CT' },
  { id: 'aug', name: 'AUG', price: 3300, key: '6', category: 'Rifles', team: 'CT' },
  { id: 'sg553', name: 'SG 553', price: 3000, key: '7', category: 'Rifles', team: 'T' },
  { id: 'nova', name: 'Nova', price: 1200, key: '1', category: 'Shotguns' },
  { id: 'awp', name: 'AWP', price: 4750, key: '1', category: 'Snipers' },
  { id: 'ssg08', name: 'SSG 08', price: 1700, key: '2', category: 'Snipers' },
];

const GRENADE_DEFS: GrenadeDef[] = [
  { id: 'he_grenade', name: 'HE Grenade', price: 300, key: '1', maxCarry: 1 },
  { id: 'smoke_grenade', name: 'Smoke Grenade', price: 300, key: '2', maxCarry: 1 },
  { id: 'flashbang', name: 'Flashbang', price: 200, key: '3', maxCarry: MAX_FLASHBANGS },
  { id: 'molotov', name: 'Molotov', price: 400, key: '4', team: 'T', maxCarry: 1 },
  { id: 'incendiary', name: 'Incendiary', price: 500, key: '5', team: 'CT', maxCarry: 1 },
  { id: 'decoy_grenade', name: 'Decoy Grenade', price: 50, key: '6', maxCarry: 1 },
];

const EQUIPMENT: EquipmentDef[] = [
  { id: 'kevlar', name: 'Kevlar Vest', price: 650, key: '6' },
  { id: 'kevlar_helmet', name: 'Kevlar + Helmet', price: 1000, key: '7' },
  { id: 'defuse_kit', name: 'Defuse Kit', price: 400, key: '8', team: 'CT' },
];

const ACCENT = '#e94560';
const BG_DARK = '#1a1a2e';
const CATEGORIES = ['Pistols', 'SMGs', 'Rifles', 'Snipers', 'Shotguns', 'Grenades', 'Equipment'] as const;
const CATEGORY_META: Record<(typeof CATEGORIES)[number], { icon: string; label: string }> = {
  Pistols: { icon: 'P', label: 'Pistols' },
  SMGs: { icon: 'S', label: 'SMGs' },
  Rifles: { icon: 'R', label: 'Rifles' },
  Snipers: { icon: '+', label: 'Snipers' },
  Shotguns: { icon: 'H', label: 'Heavy' },
  Grenades: { icon: 'G', label: 'Grenades' },
  Equipment: { icon: 'E', label: 'Equipment' },
};

/** Duration for purchase toast notifications (ms). */
export const PURCHASE_TOAST_DURATION = 1500;

/** Duration for weapon icon confirmation display (ms). */
export const PURCHASE_ICON_DURATION = 500;

/** Play a distinct purchase sound (separate from generic UI select). */
export function playPurchaseSound(): void {
  audioManager.play('ui_select', 0.6);
}

/**
 * Show a green flash animation on a money display element.
 * Exported for testability.
 */
export function flashMoneySuccess(el: HTMLElement): void {
  el.style.transition = 'color 0.15s, text-shadow 0.15s';
  el.style.color = '#88ff88';
  el.style.textShadow = '0 0 20px rgba(68,255,68,0.8)';
  setTimeout(() => {
    el.style.color = '#44ff44';
    el.style.textShadow = '0 0 10px rgba(68,255,68,0.3)';
  }, 300);
}

/**
 * Show a red flash + shake animation on a money display element.
 * Exported for testability.
 */
export function flashMoneyFail(el: HTMLElement): void {
  el.style.transition = 'color 0.15s, text-shadow 0.15s';
  el.style.color = '#ff4444';
  el.style.textShadow = '0 0 20px rgba(255,68,68,0.8)';
  el.style.animation = 'buy-shake 0.3s ease-out';
  setTimeout(() => {
    el.style.color = '#44ff44';
    el.style.textShadow = '0 0 10px rgba(68,255,68,0.3)';
    el.style.animation = '';
  }, 300);
}

/**
 * Show a purchase toast notification that fades after PURCHASE_TOAST_DURATION ms.
 */
export function showPurchaseToast(weaponName: string, cost: number): void {
  const toast = document.createElement('div');
  toast.setAttribute('data-purchase-toast', '1');
  Object.assign(toast.style, {
    position: 'fixed',
    bottom: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '8px 20px',
    background: 'rgba(0,0,0,0.8)',
    border: '1px solid #44ff44',
    borderRadius: '4px',
    color: '#44ff44',
    fontFamily: '"Courier New", monospace',
    fontSize: '14px',
    fontWeight: 'bold',
    letterSpacing: '1px',
    zIndex: '70',
    transition: 'opacity 0.3s',
    pointerEvents: 'none',
  });
  toast.textContent = `Purchase: ${weaponName} -$${cost}`;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, PURCHASE_TOAST_DURATION);
}

/**
 * Show a weapon icon briefly in center of screen as purchase confirmation.
 */
export function showPurchaseIcon(weaponName: string): void {
  const icon = document.createElement('div');
  icon.setAttribute('data-purchase-icon', '1');
  Object.assign(icon.style, {
    position: 'fixed',
    top: '40%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '12px 24px',
    background: 'rgba(0,0,0,0.6)',
    borderRadius: '6px',
    color: '#ffffff',
    fontFamily: '"Courier New", monospace',
    fontSize: '20px',
    fontWeight: 'bold',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    zIndex: '70',
    transition: 'opacity 0.2s',
    pointerEvents: 'none',
    textShadow: '0 0 10px rgba(255,255,255,0.3)',
  });
  icon.textContent = weaponName;
  document.body.appendChild(icon);

  setTimeout(() => {
    icon.style.opacity = '0';
    setTimeout(() => icon.remove(), 200);
  }, PURCHASE_ICON_DURATION);
}

// Inject shake keyframes (once, idempotent across HMR reloads via data-id lookup)
if (typeof document !== 'undefined' && !document.getElementById('cp-buy-shake-style')) {
  const style = document.createElement('style');
  style.id = 'cp-buy-shake-style';
  style.textContent = `@keyframes buy-shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-4px)} 50%{transform:translateX(4px)} 75%{transform:translateX(-2px)} }`;
  document.head.appendChild(style);
}

// BUG-W-19: Touch feedback for buy rows. mouseenter/mouseleave don't fire
// reliably on touch devices, so we also style via :active (browser-supplied
// during press) and a `.touched` class set by touchstart/touchend handlers.
// Both selectors mirror the hover treatment in createItemRow().
if (typeof document !== 'undefined' && !document.getElementById('cp-buy-touch-style')) {
  const style = document.createElement('style');
  style.id = 'cp-buy-touch-style';
  style.textContent =
    `.buy-item:active, .buy-item.touched { background: rgba(233,69,96,0.15) !important; border-color: ${ACCENT} !important; }`;
  document.head.appendChild(style);
}

export class BuyMenu {
  private overlay: HTMLElement;
  private onBuy: (itemId: string) => void;
  private onBuyGrenade: ((grenadeId: GrenadeId) => void) | null = null;
  private onBuyEquipment: ((equipmentId: string, price: number) => void) | null = null;
  private open = false;
  private currentMoney = 0;
  private buyAllowed = true;
  private playerTeam: 'T' | 'CT' | null = null;
  private buyTimeMessageTimer: ReturnType<typeof setTimeout> | null = null;
  private activeCategory: (typeof CATEGORIES)[number] = 'Pistols';
  private lossBonus: number | null = null;
  private currentGrenadeInventory: GrenadeInventory = {};
  /**
   * GP-W3: Server-authoritative set of weapon IDs the local player already
   * owns. `undefined` means the snapshot did not include the field — in that
   * case the buy gate falls open so older protocol revisions still allow
   * purchases. Updated via {@link setOwnedWeapons}.
   */
  private ownedWeapons: Record<string, boolean> | undefined = undefined;
  private presetManager = new BuyPresetManager();
  /** Items purchased this round — used for "Save Current" preset feature. */
  private purchasedThisRound: string[] = [];
  /** Mock cross-round weapon purchase history for the Recently bought strip. */
  private recentPurchases: WeaponId[] = ['ak47', 'm4a4', 'awp', 'ak47', 'mp9', 'ak47', 'p90', 'm4a4'];
  private lastRoundPurchases: Set<string> = new Set();
  /**
   * UI-H6: instance-scope AbortController for permanent listeners (document
   * keydown). Aborted only on dispose().
   */
  private abortController = new AbortController();
  /**
   * UI-H6: render-scope AbortController for per-render listeners on tabs,
   * preset buttons, weapon/equipment/grenade rows, save-dialog slots and
   * cancel buttons. Recycled at the top of render() so closures from the
   * previous render are released; otherwise the controller's abort-callback
   * chain pins detached DOM elements for the lifetime of the panel.
   */
  private renderController = new AbortController();

  constructor(onBuy: (itemId: string) => void) {
    this.onBuy = onBuy;

    this.overlay = document.createElement('div');
    this.overlay.style.cssText =
      'position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; ' +
      'background: rgba(0,0,0,0.85); z-index: 60; display: none; ' +
      'font-family: "Courier New", monospace; color: #ffffff; user-select: none; ' +
      'touch-action: manipulation; overflow-y: auto; -webkit-overflow-scrolling: touch;';
    document.body.appendChild(this.overlay);

    document.addEventListener('keydown', (e) => this.handleKey(e), {
      signal: this.abortController.signal,
    });
  }

  /** Set whether buying is allowed (true during freeze_time, false otherwise). */
  setBuyAllowed(allowed: boolean): void {
    this.buyAllowed = allowed;
  }

  /**
   * BUG-W-9: Update the displayed money balance. Called from the snapshot
   * handler so an open buy menu reflects round-end bonuses + kill bonuses
   * instead of being frozen at whatever `show()` saw. We do NOT trigger a
   * full re-render — only the money element is patched so hover/focus state
   * on tabs/rows survives the update.
   */
  setMoney(money: number): void {
    this.currentMoney = money;
    if (!this.open) return;
    const moneyEl = this.overlay.querySelector('[data-money-display]') as HTMLElement | null;
    if (moneyEl) {
      moneyEl.textContent = `$${this.currentMoney}`;
    }
  }

  /** Set the player's team for filtering team-restricted weapons. */
  setTeam(team: 'T' | 'CT' | null): void {
    this.playerTeam = team;
  }

  /** Set round loss bonus indicator (null to hide). */
  setLossBonus(bonus: number | null): void {
    this.lossBonus = bonus;
  }

  /** Set callback for grenade purchases (separate from weapon buys). */
  setOnBuyGrenade(cb: (grenadeId: GrenadeId) => void): void {
    this.onBuyGrenade = cb;
  }

  /** Set callback for equipment purchases (kevlar, helmet, defuse kit). */
  setOnBuyEquipment(cb: (equipmentId: string, price: number) => void): void {
    this.onBuyEquipment = cb;
  }

  /** Update the current grenade inventory for carry-limit display. */
  setGrenadeInventory(inv: GrenadeInventory): void {
    this.currentGrenadeInventory = { ...inv };
  }

  /**
   * GP-W3: Sync the server-authoritative ownedWeapons set. Pass `undefined`
   * when the field is absent from the snapshot to disable the gate; pass an
   * object (even empty `{}`) to apply the gate.
   */
  setOwnedWeapons(owned: Record<string, boolean> | undefined): void {
    this.ownedWeapons = owned ? { ...owned } : undefined;
  }

  /**
   * GP-W3: Returns false if the local player already owns this weapon
   * according to the latest server snapshot, true otherwise. When the server
   * has not sent ownedWeapons (legacy / pre-protocol-bump snapshot) this
   * returns true so older flows still work — the server's economy module
   * remains the final authority and will reject duplicate buys.
   */
  canBuy(weaponId: string): boolean {
    if (!this.ownedWeapons) return true;
    return this.ownedWeapons[weaponId] !== true;
  }

  private isWeaponId(id: string): id is WeaponId {
    return WEAPONS.some((weapon) => weapon.id === id);
  }

  private recordWeaponPurchase(id: string): void {
    if (!this.isWeaponId(id)) return;
    this.recentPurchases.push(id);
  }

  private getTopRecentWeapons(): Array<{ weapon: WeaponDef; count: number }> {
    const counts = new Map<WeaponId, { count: number; lastIndex: number }>();
    this.recentPurchases.forEach((id, index) => {
      const entry = counts.get(id) ?? { count: 0, lastIndex: index };
      entry.count += 1;
      entry.lastIndex = index;
      counts.set(id, entry);
    });

    return Array.from(counts.entries())
      .map(([id, meta]) => ({
        weapon: WEAPONS.find((weapon) => weapon.id === id),
        count: meta.count,
        lastIndex: meta.lastIndex,
      }))
      .filter((entry): entry is { weapon: WeaponDef; count: number; lastIndex: number } => {
        if (!entry.weapon) return false;
        return !entry.weapon.team || !this.playerTeam || entry.weapon.team === this.playerTeam;
      })
      .sort((a, b) => b.count - a.count || b.lastIndex - a.lastIndex)
      .slice(0, 3)
      .map(({ weapon, count }) => ({ weapon, count }));
  }

  /** Reset the list of items purchased this round (call at round start). */
  resetRoundPurchases(): void {
    this.lastRoundPurchases = new Set(this.purchasedThisRound);
    this.purchasedThisRound = [];
  }

  /** Clear previous-round purchase highlights at the start of freeze_time. */
  clearLastRoundPurchases(): void {
    this.lastRoundPurchases.clear();
    if (this.open) this.render();
  }

  show(money: number): void {
    this.currentMoney = money;
    this.open = true;
    this.overlay.style.display = 'block';
    this.render();
  }

  /**
   * BUG-W-12: Variant of {@link show} that also sets the round loss-streak
   * bonus in a single call. Existing callers that don't track loss bonus
   * keep using `show(money)`; callers that do can now opt-in without
   * needing two sequential calls. Passing `null` (or 0) hides the row;
   * a positive number renders the "LOSS BONUS: +$X" indicator. Leaves
   * the legacy `show(money: number): void` signature untouched so static
   * source-grep tests continue to match.
   */
  showWithLossBonus(money: number, lossBonus: number | null): void {
    this.lossBonus = lossBonus;
    this.show(money);
  }

  hide(): void {
    this.open = false;
    this.overlay.style.display = 'none';
    if (this.buyTimeMessageTimer) {
      clearTimeout(this.buyTimeMessageTimer);
      this.buyTimeMessageTimer = null;
    }
  }

  isOpen(): boolean {
    return this.open;
  }

  dispose(): void {
    // UI-H6: abort both controllers — static (constructor keydown) and
    // render-scope (per-render tab/preset/row/dialog handlers).
    this.abortController.abort();
    this.renderController.abort();
    if (this.buyTimeMessageTimer) clearTimeout(this.buyTimeMessageTimer);
    this.overlay.remove();
  }

  private handleKey(e: KeyboardEvent): void {
    // BUG-W-1 / BUG-W-2: The open/close-from-document handling for 'B' and
    // 'Escape' lived here AND in game-input-handlers, and the two listeners
    // raced — BuyMenu hid first, then the game handler saw `!isOpen()` and
    // re-opened (B) or popped settings (Escape). Both branches now live in
    // game-input-handlers as a single, ordered toggle. Number-key item
    // selection and arrow-key tab switching remain handled here.
    if (!this.open) return;

    // Tab switching with arrow keys
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const idx = CATEGORIES.indexOf(this.activeCategory);
      const next =
        e.key === 'ArrowRight'
          ? (idx + 1) % CATEGORIES.length
          : (idx - 1 + CATEGORIES.length) % CATEGORIES.length;
      this.activeCategory = CATEGORIES[next];
      this.render();
      return;
    }

    // Number keys to buy (weapons, grenades, or equipment)
    if (this.activeCategory === 'Equipment') {
      const equip = EQUIPMENT.find((eq) => eq.key === e.key);
      if (equip) {
        this.tryBuyEquipment(equip);
      }
    } else if (this.activeCategory === 'Grenades') {
      const grenade = GRENADE_DEFS.find((g) => g.key === e.key);
      if (grenade) {
        this.tryBuyGrenade(grenade);
      }
    } else {
      const weapon = WEAPONS.find((w) => w.key === e.key && w.category === this.activeCategory);
      if (!weapon) {
        // BUG-W-13: Cross-category fallback used to match the first weapon
        // with that number key regardless of team — e.g. a CT pressing '6'
        // on Pistols would attempt to buy Tec-9 (T-only) and the server
        // would reject it. Filter by the player's team so the fallback
        // only ever picks a weapon the player is allowed to buy.
        const anyWeapon = WEAPONS.find(
          (w) => w.key === e.key && (!w.team || !this.playerTeam || w.team === this.playerTeam),
        );
        if (anyWeapon) {
          this.tryBuy(anyWeapon.id, anyWeapon.price, anyWeapon.team);
        }
        return;
      }
      this.tryBuy(weapon.id, weapon.price, weapon.team);
    }
  }

  private tryBuy(id: string, price: number, team?: 'T' | 'CT'): void {
    if (!this.buyAllowed) {
      this.showBuyTimeOver();
      return;
    }
    // GP-W3: Reject buys for weapons the server says we already own. Mirror
    // the existing failure feedback path so the player sees consistent UX.
    if (!this.canBuy(id)) {
      playUIError();
      this.showPurchaseFeedback('', 0, false);
      return;
    }
    const teamOk = !team || !this.playerTeam || team === this.playerTeam;
    if (teamOk && price <= this.currentMoney) {
      this.onBuy(id);
      this.purchasedThisRound.push(id);
      this.recordWeaponPurchase(id);
      // Optimistic local update — deduct price immediately so the buy menu
      // reflects the new balance without waiting for a server snapshot.
      this.currentMoney -= price;
      playPurchaseSound();
      const weaponDef = WEAPONS.find((w) => w.id === id);
      const name = weaponDef?.name ?? id;
      this.showPurchaseFeedback(name, price, true);
      this.hide();
    } else {
      playUIError();
      this.showPurchaseFeedback('', 0, false);
    }
  }

  private tryBuyEquipment(equip: EquipmentDef): void {
    if (!this.buyAllowed) {
      this.showBuyTimeOver();
      return;
    }
    const teamOk = !equip.team || !this.playerTeam || equip.team === this.playerTeam;
    if (!teamOk) { playUIError(); this.showPurchaseFeedback('', 0, false); return; }
    if (equip.price > this.currentMoney) { playUIError(); this.showPurchaseFeedback('', 0, false); return; }

    if (this.onBuyEquipment) {
      this.onBuyEquipment(equip.id, equip.price);
    }
    this.purchasedThisRound.push(equip.id);
    this.currentMoney -= equip.price;
    playPurchaseSound();
    this.showPurchaseFeedback(equip.name, equip.price, true);
    this.hide();
  }

  private tryBuyGrenade(grenade: GrenadeDef): void {
    if (!this.buyAllowed) {
      this.showBuyTimeOver();
      return;
    }
    const teamOk = !grenade.team || !this.playerTeam || grenade.team === this.playerTeam;
    if (!teamOk) { playUIError(); this.showPurchaseFeedback('', 0, false); return; }
    if (grenade.price > this.currentMoney) { playUIError(); this.showPurchaseFeedback('', 0, false); return; }
    if (!canCarryGrenade(this.currentGrenadeInventory, grenade.id)) { playUIError(); this.showPurchaseFeedback('', 0, false); return; }

    if (this.onBuyGrenade) {
      this.onBuyGrenade(grenade.id);
    }
    this.purchasedThisRound.push(grenade.id);
    // Optimistic local update for grenade purchase
    this.currentMoney -= grenade.price;
    playPurchaseSound();
    this.showPurchaseFeedback(grenade.name, grenade.price, true);
    this.hide();
  }

  /**
   * Show purchase feedback: green flash + toast + icon on success,
   * red flash + shake on failure.
   */
  private showPurchaseFeedback(name: string, price: number, success: boolean): void {
    // Flash the money display if currently rendered
    const moneyEl = this.overlay.querySelector('[data-money-display]') as HTMLElement | null;
    if (moneyEl) {
      if (success) {
        flashMoneySuccess(moneyEl);
      } else {
        flashMoneyFail(moneyEl);
      }
    }

    if (success && name) {
      showPurchaseToast(name, price);
      showPurchaseIcon(name);
    }
  }

  private showBuyTimeOver(): void {
    const existing = this.overlay.querySelector('[data-buy-message]');
    if (existing) existing.remove();

    const msg = document.createElement('div');
    msg.setAttribute('data-buy-message', '1');
    Object.assign(msg.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: '22px',
      fontWeight: 'bold',
      color: '#ff4444',
      textShadow: '0 2px 8px rgba(0,0,0,0.8)',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      pointerEvents: 'none',
      zIndex: '100',
      fontFamily: '"Courier New", monospace',
    });
    msg.textContent = 'Buy time is over!';
    this.overlay.appendChild(msg);

    if (this.buyTimeMessageTimer) clearTimeout(this.buyTimeMessageTimer);
    this.buyTimeMessageTimer = setTimeout(() => {
      msg.remove();
    }, 1500);
  }

  private render(): void {
    // Recycle the render-scope controller so listeners attached during the
    // previous render() are released along with their DOM nodes (UI-H6).
    this.renderController.abort();
    this.renderController = new AbortController();

    // Remove children without destroying mid-gesture (avoids innerHTML = '' which
    // nukes listeners on elements that may still be processing touch/click events)
    while (this.overlay.firstChild) {
      this.overlay.removeChild(this.overlay.firstChild);
    }

    // Header section with money prominently displayed
    const header = document.createElement('div');
    Object.assign(header.style, {
      textAlign: 'center',
      padding: '20px 0 10px',
      borderBottom: `2px solid ${ACCENT}`,
      marginBottom: '16px',
      background: `linear-gradient(180deg, ${BG_DARK} 0%, transparent 100%)`,
    });

    // Title
    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '28px',
      fontWeight: 'bold',
      color: ACCENT,
      letterSpacing: '4px',
      textTransform: 'uppercase',
    });
    title.textContent = 'BUY MENU';
    header.appendChild(title);

    // Money display (prominent)
    const moneyRow = document.createElement('div');
    Object.assign(moneyRow.style, {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '20px',
      marginTop: '10px',
    });

    const moneyEl = document.createElement('div');
    moneyEl.setAttribute('data-money-display', '1');
    Object.assign(moneyEl.style, {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#44ff44',
      textShadow: '0 0 10px rgba(68,255,68,0.3)',
      letterSpacing: '2px',
    });
    moneyEl.textContent = `$${this.currentMoney}`;
    moneyRow.appendChild(moneyEl);

    // Loss bonus indicator — BUG-W-12: only show when truthy (positive bonus).
    if (this.lossBonus !== null && this.lossBonus !== undefined && this.lossBonus > 0) {
      const bonusEl = document.createElement('div');
      Object.assign(bonusEl.style, {
        fontSize: '13px',
        color: '#aaa',
        padding: '4px 10px',
        border: '1px solid #444',
        borderRadius: '3px',
        background: 'rgba(255,255,255,0.05)',
      });
      const bonusLabel = document.createElement('span');
      bonusLabel.style.color = '#888';
      bonusLabel.textContent = 'LOSS BONUS: ';
      bonusEl.appendChild(bonusLabel);
      const bonusAmount = document.createElement('span');
      bonusAmount.style.color = '#e8b849';
      bonusAmount.textContent = `+$${this.lossBonus}`;
      bonusEl.appendChild(bonusAmount);
      moneyRow.appendChild(bonusEl);
    }

    header.appendChild(moneyRow);

    // Team indicator
    if (this.playerTeam) {
      const teamEl = document.createElement('div');
      const teamColor = this.playerTeam === 'T' ? '#e8b849' : '#5b9bd5';
      Object.assign(teamEl.style, {
        fontSize: '12px',
        color: teamColor,
        marginTop: '4px',
        letterSpacing: '2px',
      });
      teamEl.textContent = this.playerTeam === 'T' ? 'TERRORIST' : 'COUNTER-TERRORIST';
      header.appendChild(teamEl);
    }

    this.overlay.appendChild(header);

    // Buy time warning
    if (!this.buyAllowed) {
      const warning = document.createElement('div');
      Object.assign(warning.style, {
        textAlign: 'center',
        fontSize: '16px',
        color: '#ff4444',
        marginBottom: '12px',
        fontWeight: 'bold',
        letterSpacing: '1px',
      });
      warning.textContent = 'BUY TIME IS OVER';
      this.overlay.appendChild(warning);
    }

    this.renderCategoryNav(this.overlay);

    // Presets row
    this.renderPresets(this.overlay);

    // Content area
    const content = document.createElement('div');
    Object.assign(content.style, {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 40px',
    });

    this.renderRecentlyBought(content);

    if (this.activeCategory === 'Equipment') {
      this.renderEquipment(content);
    } else if (this.activeCategory === 'Grenades') {
      this.renderGrenades(content);
    } else {
      this.renderWeapons(content, this.activeCategory);
    }

    this.overlay.appendChild(content);

    // Close hint
    const hint = document.createElement('div');
    Object.assign(hint.style, {
      textAlign: 'center',
      color: '#555',
      fontSize: '12px',
      marginTop: '30px',
      letterSpacing: '1px',
    });
    hint.textContent = '[B] or [ESC] Close  |  [LEFT/RIGHT] Switch Tab';
    this.overlay.appendChild(hint);
  }

  private renderCategoryNav(parent: HTMLElement): void {
    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '6px',
      flexWrap: 'wrap',
      padding: '0 40px',
      marginBottom: '12px',
    });

    const label = document.createElement('div');
    Object.assign(label.style, {
      fontSize: '10px',
      color: '#888',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginRight: '6px',
    });
    label.textContent = 'Categories';
    wrapper.appendChild(label);

    for (const cat of CATEGORIES) {
      const meta = CATEGORY_META[cat];
      const isActive = cat === this.activeCategory;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.setAttribute('aria-pressed', String(isActive));
      Object.assign(tab.style, {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        minHeight: '32px',
        padding: '5px 10px',
        border: isActive ? `1px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.12)',
        borderRadius: '3px',
        background: isActive ? 'rgba(233,69,96,0.18)' : 'rgba(255,255,255,0.04)',
        color: isActive ? '#ffffff' : '#8a8a8a',
        cursor: 'pointer',
        fontFamily: '"Courier New", monospace',
        fontSize: '11px',
        fontWeight: 'bold',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        transition: 'all 0.15s',
      });

      const icon = document.createElement('span');
      Object.assign(icon.style, {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '18px',
        height: '18px',
        borderRadius: '2px',
        background: isActive ? ACCENT : 'rgba(255,255,255,0.08)',
        color: '#ffffff',
        fontSize: '10px',
        lineHeight: '18px',
      });
      icon.textContent = meta.icon;

      const name = document.createElement('span');
      name.textContent = meta.label;
      tab.append(icon, name);

      const sig = { signal: this.renderController.signal };
      tab.addEventListener('click', () => {
        if (this.activeCategory === cat) return;
        playUISelect();
        this.activeCategory = cat;
        this.render();
      }, sig);
      tab.addEventListener('mouseenter', () => {
        if (isActive) return;
        tab.style.color = '#dddddd';
        tab.style.borderColor = 'rgba(255,255,255,0.28)';
      }, sig);
      tab.addEventListener('mouseleave', () => {
        if (isActive) return;
        tab.style.color = '#8a8a8a';
        tab.style.borderColor = 'rgba(255,255,255,0.12)';
      }, sig);
      wrapper.appendChild(tab);
    }

    parent.appendChild(wrapper);
  }

  private renderPresets(parent: HTMLElement): void {
    const side = this.playerTeam;
    if (!side) return; // No team assigned — skip presets

    const recommended = this.presetManager.getRecommendedBuy(side, this.currentMoney);
    const presets = this.presetManager.getPresets(side);

    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 40px',
      marginBottom: '12px',
    });

    // "PRESETS" label
    const label = document.createElement('div');
    Object.assign(label.style, {
      fontSize: '11px',
      color: '#888',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginRight: '8px',
      fontFamily: '"Courier New", monospace',
    });
    label.textContent = 'PRESETS';
    wrapper.appendChild(label);

    for (let i = 0; i < presets.length; i++) {
      const preset = presets[i];
      const cost = this.presetManager.getPresetCost(side, i);
      const canAfford = this.buyAllowed && cost <= this.currentMoney;
      const isRecommended = i === recommended;

      const btn = document.createElement('div');
      btn.setAttribute('data-preset-slot', String(i));
      Object.assign(btn.style, {
        padding: '8px 16px',
        fontSize: '12px',
        fontWeight: 'bold',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        cursor: canAfford ? 'pointer' : 'default',
        border: isRecommended ? '2px solid #44ff44' : '1px solid rgba(255,255,255,0.15)',
        borderRadius: '4px',
        background: isRecommended
          ? 'rgba(68,255,68,0.08)'
          : 'rgba(255,255,255,0.04)',
        color: canAfford ? '#ffffff' : '#666',
        opacity: canAfford ? '1' : '0.6',
        transition: 'all 0.15s',
        fontFamily: '"Courier New", monospace',
        textAlign: 'center',
        minWidth: '100px',
      });

      // Name row
      const nameSpan = document.createElement('div');
      nameSpan.style.marginBottom = '2px';
      nameSpan.textContent = preset.name;
      btn.appendChild(nameSpan);

      const summarySpan = document.createElement('div');
      Object.assign(summarySpan.style, {
        fontSize: '9px',
        color: canAfford ? '#aaa' : '#555',
        fontWeight: 'normal',
        textTransform: 'none',
        letterSpacing: '0',
        marginBottom: '3px',
        maxWidth: '130px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      });
      summarySpan.textContent = summarizePresetLoadout(preset.items);
      btn.appendChild(summarySpan);

      // Cost row
      const costSpan = document.createElement('div');
      Object.assign(costSpan.style, {
        fontSize: '10px',
        color: canAfford ? '#44ff44' : '#cc3333',
        fontWeight: 'normal',
      });
      costSpan.textContent = cost > 0 ? `$${cost}` : 'FREE';
      btn.appendChild(costSpan);

      if (canAfford) {
        const sig = { signal: this.renderController.signal };
        btn.addEventListener('mouseenter', () => {
          btn.style.background = 'rgba(233,69,96,0.15)';
          btn.style.borderColor = ACCENT;
        }, sig);
        btn.addEventListener('mouseleave', () => {
          btn.style.background = isRecommended
            ? 'rgba(68,255,68,0.08)'
            : 'rgba(255,255,255,0.04)';
          btn.style.borderColor = isRecommended
            ? '#44ff44'
            : 'rgba(255,255,255,0.15)';
        }, sig);
        btn.addEventListener('click', () => {
          this.executePreset(side, i);
        }, sig);
      }

      wrapper.appendChild(btn);
    }

    // "Save Current" button
    if (this.purchasedThisRound.length > 0) {
      const saveBtn = document.createElement('div');
      Object.assign(saveBtn.style, {
        padding: '8px 12px',
        fontSize: '10px',
        fontWeight: 'bold',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        cursor: 'pointer',
        border: `1px solid ${ACCENT}`,
        borderRadius: '4px',
        background: 'rgba(233,69,96,0.08)',
        color: ACCENT,
        transition: 'all 0.15s',
        fontFamily: '"Courier New", monospace',
        textAlign: 'center',
        marginLeft: '8px',
      });
      saveBtn.textContent = 'SAVE CURRENT';
      const sig = { signal: this.renderController.signal };
      saveBtn.addEventListener('mouseenter', () => {
        saveBtn.style.background = 'rgba(233,69,96,0.2)';
      }, sig);
      saveBtn.addEventListener('mouseleave', () => {
        saveBtn.style.background = 'rgba(233,69,96,0.08)';
      }, sig);
      saveBtn.addEventListener('click', () => {
        this.showSavePresetDialog(side);
      }, sig);
      wrapper.appendChild(saveBtn);
    }

    parent.appendChild(wrapper);
  }

  /** Execute a preset — buy all items in sequence if affordable. */
  private executePreset(side: 'T' | 'CT', slot: number): void {
    if (!this.buyAllowed) {
      this.showBuyTimeOver();
      return;
    }

    const preset = this.presetManager.getPreset(side, slot);
    const grenadeIds = new Set(GRENADE_DEFS.map((g) => g.id as string));
    const equipmentIds = new Set(EQUIPMENT.map((e) => e.id));
    const weaponIds = new Set(WEAPONS.map((w) => w.id));

    for (const itemId of preset.items) {
      if (this.currentMoney <= 0) break;

      if (grenadeIds.has(itemId)) {
        const grenade = GRENADE_DEFS.find((g) => g.id === itemId);
        if (grenade && grenade.price <= this.currentMoney) {
          const teamOk = !grenade.team || !this.playerTeam || grenade.team === this.playerTeam;
          if (teamOk && canCarryGrenade(this.currentGrenadeInventory, grenade.id)) {
            // BUG-W-14: Route preset grenade buys through the same
            // `onBuyGrenade` callback as `tryBuyGrenade` so the host's
            // `ctx.grenadeInventory` is the single source of truth. Bail
            // out before mutating local state if the callback isn't wired
            // — otherwise the local mirror would drift from ctx.
            if (!this.onBuyGrenade) continue;
            this.onBuyGrenade(grenade.id);
            this.purchasedThisRound.push(grenade.id);
            this.currentMoney -= grenade.price;
            // Local mirror bump for within-preset carry-limit checks; the
            // next snapshot's `setGrenadeInventory` call will re-sync this
            // mirror back to the host's `ctx.grenadeInventory`.
            this.currentGrenadeInventory[grenade.id] =
              (this.currentGrenadeInventory[grenade.id] ?? 0) + 1;
          }
        }
      } else if (equipmentIds.has(itemId)) {
        const equip = EQUIPMENT.find((e) => e.id === itemId);
        if (equip && equip.price <= this.currentMoney) {
          const teamOk = !equip.team || !this.playerTeam || equip.team === this.playerTeam;
          if (teamOk && this.onBuyEquipment) {
            this.onBuyEquipment(equip.id, equip.price);
            this.purchasedThisRound.push(equip.id);
            this.currentMoney -= equip.price;
          }
        }
      } else if (this.isWeaponId(itemId) && weaponIds.has(itemId)) {
        const weapon = WEAPONS.find((w) => w.id === itemId);
        if (weapon && weapon.price <= this.currentMoney) {
          const teamOk = !weapon.team || !this.playerTeam || weapon.team === this.playerTeam;
          if (teamOk) {
            this.onBuy(weapon.id);
            this.purchasedThisRound.push(weapon.id);
            this.recordWeaponPurchase(weapon.id);
            this.currentMoney -= weapon.price;
          }
        }
      }
    }

    this.hide();
  }

  /** Show a simple dialog to save current purchases as a preset. */
  private showSavePresetDialog(side: 'T' | 'CT'): void {
    // Simple slot selection overlay
    const dialog = document.createElement('div');
    Object.assign(dialog.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: BG_DARK,
      border: `2px solid ${ACCENT}`,
      borderRadius: '6px',
      padding: '20px',
      zIndex: '110',
      fontFamily: '"Courier New", monospace',
      textAlign: 'center',
      minWidth: '260px',
    });

    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '14px',
      fontWeight: 'bold',
      color: ACCENT,
      letterSpacing: '2px',
      marginBottom: '14px',
      textTransform: 'uppercase',
    });
    title.textContent = 'SAVE TO SLOT';
    dialog.appendChild(title);

    const presets = this.presetManager.getPresets(side);
    for (let i = 0; i < presets.length; i++) {
      const slotBtn = document.createElement('div');
      Object.assign(slotBtn.style, {
        padding: '8px 16px',
        margin: '6px 0',
        fontSize: '12px',
        fontWeight: 'bold',
        cursor: 'pointer',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '3px',
        background: 'rgba(255,255,255,0.04)',
        color: '#ffffff',
        transition: 'all 0.15s',
        letterSpacing: '1px',
      });
      const slotName = document.createElement('div');
      slotName.textContent = `${i + 1}. ${presets[i].name}`;
      slotBtn.appendChild(slotName);

      const slotSummary = document.createElement('div');
      Object.assign(slotSummary.style, {
        fontSize: '10px',
        fontWeight: 'normal',
        color: '#888',
        marginTop: '3px',
      });
      slotSummary.textContent = summarizePresetLoadout(presets[i].items);
      slotBtn.appendChild(slotSummary);
      const sig = { signal: this.renderController.signal };
      slotBtn.addEventListener('mouseenter', () => {
        slotBtn.style.background = 'rgba(233,69,96,0.15)';
        slotBtn.style.borderColor = ACCENT;
      }, sig);
      slotBtn.addEventListener('mouseleave', () => {
        slotBtn.style.background = 'rgba(255,255,255,0.04)';
        slotBtn.style.borderColor = 'rgba(255,255,255,0.15)';
      }, sig);
      slotBtn.addEventListener('click', () => {
        const custom: BuyPreset = {
          name: presets[i].name, // Keep slot name
          items: [...this.purchasedThisRound],
        };
        this.presetManager.savePreset(side, i, custom);
        dialog.remove();
        this.render(); // Re-render to show updated presets
      }, sig);
      dialog.appendChild(slotBtn);
    }

    // Cancel button
    const cancelBtn = document.createElement('div');
    Object.assign(cancelBtn.style, {
      padding: '6px 16px',
      marginTop: '10px',
      fontSize: '11px',
      cursor: 'pointer',
      color: '#888',
      letterSpacing: '1px',
    });
    cancelBtn.textContent = '[ESC] Cancel';
    cancelBtn.addEventListener('click', () => dialog.remove(), {
      signal: this.renderController.signal,
    });
    dialog.appendChild(cancelBtn);

    this.overlay.appendChild(dialog);
  }

  private renderRecentlyBought(parent: HTMLElement): void {
    const recent = this.getTopRecentWeapons();
    if (recent.length === 0) return;

    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, {
      width: '100%',
      maxWidth: '400px',
      marginBottom: '10px',
    });

    const label = document.createElement('div');
    Object.assign(label.style, {
      fontSize: '10px',
      color: '#888',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginBottom: '6px',
    });
    label.textContent = 'Recently bought';
    wrapper.appendChild(label);

    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'grid',
      gridTemplateColumns: `repeat(${recent.length}, minmax(0, 1fr))`,
      gap: '6px',
    });

    for (const { weapon, count } of recent) {
      const teamRestricted =
        weapon.team !== undefined && this.playerTeam !== null && weapon.team !== this.playerTeam;
      const owned = !this.canBuy(weapon.id);
      const canAfford = this.buyAllowed && weapon.price <= this.currentMoney && !teamRestricted && !owned;
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.disabled = !canAfford;
      Object.assign(chip.style, {
        padding: '7px 8px',
        background: canAfford ? 'rgba(232,184,73,0.10)' : 'rgba(255,255,255,0.035)',
        border: canAfford ? '1px solid rgba(232,184,73,0.45)' : '1px solid rgba(255,255,255,0.08)',
        borderRadius: '3px',
        color: canAfford ? '#ffffff' : '#666',
        cursor: canAfford ? 'pointer' : 'default',
        fontFamily: '"Courier New", monospace',
        textAlign: 'left',
        minHeight: '42px',
      });

      const name = document.createElement('div');
      Object.assign(name.style, {
        fontSize: '11px',
        fontWeight: 'bold',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      });
      name.textContent = weapon.name;
      chip.appendChild(name);

      const meta = document.createElement('div');
      Object.assign(meta.style, {
        fontSize: '9px',
        color: canAfford ? '#e8b849' : '#555',
        marginTop: '2px',
      });
      meta.textContent = `${count}x  $${weapon.price}`;
      chip.appendChild(meta);

      if (canAfford) {
        const sig = { signal: this.renderController.signal };
        chip.addEventListener('mouseenter', () => {
          chip.style.background = 'rgba(233,69,96,0.15)';
          chip.style.borderColor = ACCENT;
        }, sig);
        chip.addEventListener('mouseleave', () => {
          chip.style.background = 'rgba(232,184,73,0.10)';
          chip.style.borderColor = 'rgba(232,184,73,0.45)';
        }, sig);
        chip.addEventListener('click', () => {
          this.tryBuy(weapon.id, weapon.price, weapon.team);
        }, sig);
      }

      row.appendChild(chip);
    }

    wrapper.appendChild(row);
    parent.appendChild(wrapper);
  }

  private renderWeapons(container: HTMLElement, category: string): void {
    const col = document.createElement('div');
    Object.assign(col.style, { minWidth: '320px', maxWidth: '400px', width: '100%' });

    // Show all weapons in this category, including team-restricted ones
    const items = WEAPONS.filter((w) => w.category === category);
    for (const w of items) {
      const teamRestricted =
        w.team !== undefined && this.playerTeam !== null && w.team !== this.playerTeam;
      // GP-W3: a weapon is unbuyable if the player already owns it. Owned
      // rows render with the same disabled treatment as team-restricted
      // ones (greyed out, not red — owned isn't an error).
      const owned = !this.canBuy(w.id);
      const canAfford = this.buyAllowed && w.price <= this.currentMoney && !teamRestricted && !owned;
      const boughtLastRound = this.lastRoundPurchases.has(w.id);

      const row = this.createItemRow(
        w.key,
        w.name,
        w.price,
        canAfford,
        teamRestricted || owned,
        w.team ?? null,
        () => {
          this.tryBuy(w.id, w.price, w.team);
        },
        owned ? 'OWNED' : undefined,
        boughtLastRound,
      );
      col.appendChild(row);
    }

    container.appendChild(col);
  }

  private renderEquipment(container: HTMLElement): void {
    const col = document.createElement('div');
    Object.assign(col.style, { minWidth: '320px', maxWidth: '400px', width: '100%' });

    for (const eq of EQUIPMENT) {
      const teamRestricted =
        eq.team !== undefined && this.playerTeam !== null && eq.team !== this.playerTeam;
      const canAfford = this.buyAllowed && eq.price <= this.currentMoney && !teamRestricted;

      const row = this.createItemRow(
        eq.key,
        eq.name,
        eq.price,
        canAfford,
        teamRestricted,
        eq.team ?? null,
        () => {
          this.tryBuyEquipment(eq);
        },
      );
      col.appendChild(row);
    }

    container.appendChild(col);
  }

  private renderGrenades(container: HTMLElement): void {
    const col = document.createElement('div');
    Object.assign(col.style, { minWidth: '320px', maxWidth: '400px', width: '100%' });

    for (const g of GRENADE_DEFS) {
      const teamRestricted =
        g.team !== undefined && this.playerTeam !== null && g.team !== this.playerTeam;
      const currentCount = this.currentGrenadeInventory[g.id] ?? 0;
      const atMax = !canCarryGrenade(this.currentGrenadeInventory, g.id);
      const canAfford = this.buyAllowed && g.price <= this.currentMoney && !teamRestricted && !atMax;

      const row = this.createItemRow(
        g.key,
        g.name,
        g.price,
        canAfford,
        teamRestricted,
        g.team ?? null,
        () => {
          this.tryBuyGrenade(g);
        },
        `${currentCount}/${g.maxCarry}`,
      );
      col.appendChild(row);
    }

    container.appendChild(col);
  }

  private createItemRow(
    key: string,
    name: string,
    price: number,
    canAfford: boolean,
    teamRestricted: boolean,
    team: 'T' | 'CT' | null,
    onClick: () => void,
    carryCount?: string,
    boughtLastRound = false,
  ): HTMLElement {
    const row = document.createElement('div');

    // Determine colors
    let nameColor = '#ffffff';
    let priceColor = '#44ff44';
    let opacity = '1';
    let cursor = 'pointer';

    if (teamRestricted) {
      nameColor = '#555555';
      priceColor = '#555555';
      opacity = '0.4';
      cursor = 'default';
    } else if (!canAfford) {
      nameColor = '#cc3333';
      priceColor = '#cc3333';
      opacity = '0.7';
      cursor = 'default';
    }

    row.classList.add('buy-item');

    Object.assign(row.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 14px',
      marginBottom: '4px',
      background: boughtLastRound ? 'rgba(232,184,73,0.08)' : 'rgba(255,255,255,0.04)',
      borderRadius: '3px',
      cursor,
      opacity,
      border: boughtLastRound ? '1px solid rgba(232,184,73,0.35)' : '1px solid rgba(255,255,255,0.08)',
      transition: 'all 0.1s',
      minWidth: '48px',
      minHeight: '48px',
    });

    // Team tag
    let teamTag = '';
    if (team) {
      const tagColor = team === 'T' ? '#e8b849' : '#5b9bd5';
      teamTag = `<span style="color:${tagColor};font-size:10px;margin-right:6px;border:1px solid ${tagColor};padding:1px 4px;border-radius:2px;">${team}</span>`;
    }

    const carryTag = carryCount
      ? `<span style="color:#888;font-size:11px;margin-left:8px;border:1px solid #555;padding:1px 4px;border-radius:2px;">${carryCount}</span>`
      : '';
    const lastRoundTag = boughtLastRound
      ? '<span title="LAST ROUND" style="color:#e8b849;font-size:10px;margin-left:8px;border:1px solid rgba(232,184,73,0.7);padding:1px 4px;border-radius:2px;text-transform:uppercase;">&#x21BB; last</span>'
      : '';

    row.innerHTML =
      `<div style="display:flex;align-items:center;">` +
      `<span style="color:#666;font-size:11px;margin-right:10px;min-width:20px;">[${key}]</span>` +
      teamTag +
      `<span style="font-size:14px;color:${nameColor};">${name}</span>` +
      carryTag +
      lastRoundTag +
      `</div>` +
      `<span style="font-size:13px;color:${priceColor};font-weight:bold;">$${price}</span>`;

    if (canAfford && !teamRestricted) {
      const sig = { signal: this.renderController.signal };
      const applyHover = (): void => {
        row.style.background = 'rgba(233,69,96,0.15)';
        row.style.borderColor = ACCENT;
      };
      const clearHover = (): void => {
        row.style.background = boughtLastRound ? 'rgba(232,184,73,0.08)' : 'rgba(255,255,255,0.04)';
        row.style.borderColor = boughtLastRound ? 'rgba(232,184,73,0.35)' : 'rgba(255,255,255,0.08)';
      };
      row.addEventListener('mouseenter', applyHover, sig);
      row.addEventListener('mouseleave', clearHover, sig);
      // BUG-W-19: mouseenter/mouseleave don't fire reliably on touch.
      // Mirror the hover styling via touchstart/touchend + a `.touched`
      // class so mobile players get the same visual feedback.
      row.addEventListener('touchstart', () => {
        row.classList.add('touched');
        applyHover();
      }, { ...sig, passive: true });
      const releaseTouch = (): void => {
        row.classList.remove('touched');
        clearHover();
      };
      row.addEventListener('touchend', releaseTouch, sig);
      row.addEventListener('touchcancel', releaseTouch, sig);
      row.addEventListener('click', onClick, sig);
    }

    return row;
  }
}
